import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Box, 
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Swal from 'sweetalert2';

// Components
import { Column } from "../../components/common/CustomDataTable/CustomDataTable.tsx";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData, CommonFunctions } from "../PageImports.ts";
import ErrorPage from "../Error/ErrorPage.tsx";
import Loader from "../../utils/Loader.tsx";

// Utilities
import { axiosFunction } from '../../components/commonFunctions/axiosFunction.ts';
import { v2ReactURL } from '../../utils/constant.ts';
import { capitalizeFirstChar, CheckedValue, debounce, ErrorState, Pagination } from '../Contact/MainDataTable.tsx';

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

const { toastMessage } = CommonFunctions;

interface SubscriptionProps {
  navUrl: string;
}

interface SubscriptionData {
  customSubscriptionId: number;
  customSubscriptionNo: string;
  sundayQty: number;
  mondayQty: number;
  tuesdayQty: number;
  wednesdayQty: number;
  thursdayQty: number;
  fridayQty: number;
  saturdayQty: number;
  contactId: number;
  productVarientId: number;
  productId: number | null;
  productName: string;
  customerName: string;
  startDate: string;
  endDate: string | null;
  modifiedOn: string;
  isSubscriptionActive: number;
  isSubscriptionCancel: number;
  subscriptionType: string;
  timeSlot: string | null;
}

const Subscription: React.FC<SubscriptionProps> = ({ navUrl }) => {
  const tabView = navUrl;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<SubscriptionData[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });
  const [pageOption, setPageOption] = useState<string>('15');
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<ErrorState | null>(null);
  const [checkedValue, setCheckedValue] = useState<CheckedValue>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionData | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<SubscriptionData | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const fetchData = async (
    searchValue: string,
    startIndex: number,
    pageLength: number,
  ): Promise<void> => {
    setLoadingData(true);
    const payloadData = {
      searchValue: searchValue,
      length: pageLength,
      start: startIndex,
      draw: 1,
    };

    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/subscription/datatable`,
        "POST",
        payloadData
      );
      
      const responseData = res?.response?.data || res?.data || res;
      
      if (responseData && responseData.response) {
        const subscriptionData = responseData.response;
        
        if (subscriptionData.data && Array.isArray(subscriptionData.data)) {
          const subscriptionList = subscriptionData.data.map((col: SubscriptionData, index: number) => ({
            ...col,
            index: index + 1,
            checked: false,
          }));
          
          setPagination(subscriptionData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: subscriptionData.data.length
          });
          setRows(subscriptionList);
        }
      } else if (responseData && Array.isArray(responseData.data)) {
        const subscriptionList = responseData.data.map((col: SubscriptionData, index: number) => ({
          ...col,
          index: index + 1,
          checked: false,
        }));
        
        setPagination(responseData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: responseData.data.length
          });
        setRows(subscriptionList);
      } else {
        throw new Error("Invalid API response format");
      }
      
      setIsLoading(false);
      setLoadingData(false);
      
    } catch (error: any) {
      console.error("Error fetching subscription data:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK" || error.message === "network error") {
      setError({
        code: "502",
        message: "Hang tight! Our subscription service is undergoing maintenance. We'll be back to serving you real soon!",
      });
    } else if (error.response?.status === 400) {
      setError({
        code: "400",
        message: "Oops, your request got lost in translation! Maybe try again with simpler instructions?",
      });
    } else if (error.response?.status === 404) {
      setError({
        code: "404",
        message: "Oopsie! Looks like this page went on vacation. Maybe try a different path?",
      });
    } else if (error.response?.status === 500) {
      setError({
        code: "500",
        message: "Uh-oh, our server's taking a coffee break. It'll be back to managing subscriptions soon!",
      });
    } else {
      setError({
        code: "500",
        message: "An unexpected error occurred while fetching subscription data.",
      });
    }
    
    toastMessage("Error fetching subscription details");
    setLoadingData(false);
    setIsLoading(false);
  };

  // Handle Action Menu Open - Fixed positioning
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, row: SubscriptionData) => {
    event.stopPropagation(); // Prevent event bubbling
    
    const anchorElement = event.currentTarget;
    const rect = anchorElement.getBoundingClientRect();
    
    // Calculate position to open above the icon
    setMenuPosition({
      top: rect.top - 10, // Position above the icon
      left: rect.left,
    });
    
    setSelectedRow(row);
  };

  // Handle Action Menu Close
  const handleActionMenuClose = () => {
    setMenuPosition(null);
    // setActionMenuAnchor(null);
    setSelectedRow(null);
  };

  // Handle View Details
  // const handleViewDetails = () => {
  //   if (selectedRow) {
  //     handleOpenSubscriptionDetails(selectedRow);
  //   }
  //   handleActionMenuClose();
  // };

  // Handle Subscription Status Change
  const handleSubscriptionStatusChange = async (newStatus: number) => {
    if (!selectedRow) return;

    const statusConfig = {
      1: { // Active
        action: 'activate', 
        title: 'Activate Subscription', 
        text: `Are you sure you want to activate subscription #${selectedRow.customSubscriptionId}?`,
        confirmText: 'Yes, Activate',
        successText: 'Subscription activated successfully!',
        confirmColor: '#4caf50'
      },
      2: { // Pause
        action: 'pause', 
        title: 'Pause Subscription', 
        text: `Are you sure you want to pause subscription #${selectedRow.customSubscriptionId}?`,
        confirmText: 'Yes, Pause',
        successText: 'Subscription paused successfully!',
        confirmColor: '#ffa726'
      },
      3: { // Cancel
        action: 'cancel', 
        title: 'Cancel Subscription', 
        text: `Are you sure you want to cancel subscription #${selectedRow.customSubscriptionId}? This action cannot be undone.`,
        confirmText: 'Yes, Cancel',
        successText: 'Subscription canceled successfully!',
        confirmColor: '#f44336'
      }
    };

    const config = statusConfig[newStatus as keyof typeof statusConfig];

    const { isConfirmed } = await Swal.fire({
      title: config.title,
      text: config.text,
      icon: newStatus === 3 ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: config.confirmText,
      cancelButtonText: 'Cancel',
      confirmButtonColor: config.confirmColor,
      cancelButtonColor: '#6c757d',
    });

    if (!isConfirmed) {
      handleActionMenuClose();
      return;
    }

    setActionLoading(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/subscription/activeDeactive?id=${selectedRow.customSubscriptionId}&result=${newStatus}`,
        "POST",
        {},
        false
      );

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: config.successText,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3085d6',
        });
        handleFetchData(); // Refresh the data
      } else {
        throw new Error(res.data?.message || `Failed to ${config.action} subscription`);
      }
    } catch (error: any) {
      console.error(`Error ${config.action}ing subscription:`, error);
      Swal.fire({
        title: 'Error!',
        text: `Error ${config.action}ing subscription: ${error.message}`,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    } finally {
      setActionLoading(false);
      handleActionMenuClose();
    }
  };

  // Open subscription details modal
  const handleOpenSubscriptionDetails = (subscription: SubscriptionData) => {
    setSelectedSubscription(subscription);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleFetchData = async (): Promise<void> => {
    await fetchData(
      searchValue,
      pagination.page * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const handleChangePageOption = (e: { target: { value: any } }) => {
    const selectedValue = e.target.value;
    setPageOption(parseInt(selectedValue).toString());
    fetchData(
      searchValue,
      0,
      parseInt(selectedValue),
    );
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(
      searchValue,
      (page - 1) * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  const handleSearchData = useCallback(
    debounce((searchValue: string) => {
      fetchData(
        searchValue,
        0,
        parseInt(pageOption),
      );
    }, 500),
    [pageOption]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getDayQuantity = (day: string, subscription: SubscriptionData) => {
    switch (day.toLowerCase()) {
      case 'sunday': return subscription.sundayQty;
      case 'monday': return subscription.mondayQty;
      case 'tuesday': return subscription.tuesdayQty;
      case 'wednesday': return subscription.wednesdayQty;
      case 'thursday': return subscription.thursdayQty;
      case 'friday': return subscription.fridayQty;
      case 'saturday': return subscription.saturdayQty;
      default: return 0;
    }
  };

  // Status logic based on isSubscriptionActive field only
  const getStatusLabel = (row: SubscriptionData) => {
    switch (row.isSubscriptionActive) {
      case 1:
        return "Active";
      case 2:
        return "Paused";
      case 3:
        return "Canceled";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (row: SubscriptionData) => {
    switch (row.isSubscriptionActive) {
      case 1:
        return "success";   // green for Active
      case 2:
        return "warning";   // orange/yellow for Paused
      case 3:
        return "error";     // red for Canceled
      default:
        return "default";
    }
  };

  // Check if subscription is canceled
  const isSubscriptionCanceled = (row: SubscriptionData) => {
    return row.isSubscriptionActive === 3;
  };

  // Check if subscription is active
  const isSubscriptionActive = (row: SubscriptionData) => {
    return row.isSubscriptionActive === 1;
  };

  // Check if subscription is paused
  const isSubscriptionPaused = (row: SubscriptionData) => {
    return row.isSubscriptionActive === 2;
  };

  const columnsDataTableSubscription: Column[] = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 5,
      defaultChecked: true,
      renderCell: (params: any) => (
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: "13px" }}>
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
    {
      field: "customSubscriptionNo",
      headerName: "Subscription No",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          fontWeight="500"
          sx={{ 
            fontSize: "13px",
            cursor: 'pointer', 
            color: 'primary.main',
            textDecoration: 'underline',
            '&:hover': { color: 'primary.dark' }
          }}
          onClick={() => handleOpenSubscriptionDetails(params.row)}
        >
          {params.row.customSubscriptionNo}
        </Typography>
      ),
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500" sx={{ fontSize: "13px" }}>
          {params.row.customerName}
        </Typography>
      ),
    },
    {
      field: "productName",
      headerName: "Product",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "13px" }}>
          {params.row.productName}
        </Typography>
      ),
    },
    {
      field: "subscriptionType",
      headerName: "Type",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip 
          label={params.row.subscriptionType?.trim()} 
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: "13px", height: "24px" }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip 
          label={getStatusLabel(params.row)}
          size="small"
          color={getStatusColor(params.row)}
          sx={{ 
            fontSize: "13px",
            height: "24px",
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => {
        // If subscription is canceled, show dash instead of menu icon
        if (isSubscriptionCanceled(params.row)) {
          return (
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: "13px" }}>
              -
            </Typography>
          );
        }
        
        return (
          <IconButton
            size="small"
            onClick={(e) => handleActionMenuOpen(e, params.row)}
            disabled={actionLoading}
            sx={{
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  const toggleFilter = (column: Column) => {
    setSearchInputValue("");
    if (openFilter === column.field) {
      setOpenFilter(null);
    } else {
      setOpenFilter(column.field);
    }
  };

  const handleInputFocus = (field: string) => {
    setOpenFilter(field);
  };

  const handleApplyFilterBtn = () => {
    // Filter implementation if needed
  };

  if (tabView === '') {
    return (
      <ErrorPage
        error={{
          code: '403',
          message: "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{navUrl}</title>
      </Helmet>
      
      {/* Action Menu with Fixed Positioning */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
        open={Boolean(menuPosition)}
        onClose={handleActionMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 1,
            minWidth: 140,
            zIndex: 9999,
            mt: -0.5, // Fine-tune positioning
          }
        }}
      >
        {/* View Details Menu Item - Always show
        <MenuItem 
          onClick={handleViewDetails}
          sx={{ 
            fontSize: '13px',
            color: 'primary.main',
            py: 1,
          }}
        >
          View Details
        </MenuItem> */}
        
        {/* Show Active option only if subscription is paused */}
        {selectedRow && isSubscriptionPaused(selectedRow) && (
          <MenuItem 
            onClick={() => handleSubscriptionStatusChange(1)}
            sx={{ 
              fontSize: '13px',
              color: 'success.main',
              py: 1,
            }}
          >
            Active
          </MenuItem>
        )}
        
        {/* Show Pause option only if subscription is active */}
        {selectedRow && isSubscriptionActive(selectedRow) && (
          <MenuItem 
            onClick={() => handleSubscriptionStatusChange(2)}
            sx={{ 
              fontSize: '13px',
              color: 'warning.main',
              py: 1,
            }}
          >
            Pause
          </MenuItem>
        )}
        
        {/* Show Cancel option only if subscription is NOT canceled */}
        {selectedRow && !isSubscriptionCanceled(selectedRow) && (
          <MenuItem 
            onClick={() => handleSubscriptionStatusChange(3)}
            sx={{ 
              fontSize: '13px', 
              color: 'error.main',
              py: 1,
            }}
          >
            Cancel
          </MenuItem>
        )}
      </Menu>

      {/* Subscription Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 20px rgba(0,0,0,0.15)",
            backgroundColor: "#fff",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "14px",
            color: "#636161",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "1px solid #eee",
            backgroundColor: "#fafafa",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            color: "#636161",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#636161",
              }}
            >
              Subscription Details
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              sx={{
                backgroundColor: "#f0f0f0",
                "&:hover": { backgroundColor: "#e0e0e0" },
                color: "#636161",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            pt: 2,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "14px",
            color: "#636161",
          }}
        >
          {selectedSubscription && (
            <Box>
              {/* Basic Information - 2 columns */}
              <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
                {[
                  ["Subscription No", selectedSubscription.customSubscriptionNo],
                  ["Product Name", selectedSubscription.productName],
                  ["Customer Name", selectedSubscription.customerName],
                  [
                    "Subscription Status",
                    <Chip
                      key="subscription-status"
                      label={getStatusLabel(selectedSubscription)}
                      size="small"
                      sx={{
                        fontWeight: 400,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: "12px",
                        height: 22,
                        color: getStatusColor(selectedSubscription) === 'error' ? "#F44336" : 
                               (getStatusColor(selectedSubscription) === 'success' ? "#2e7d32" : "#ff9800"),
                        backgroundColor: "#ffffff",
                        borderColor: getStatusColor(selectedSubscription) === 'error' ? "#F44336" : 
                                    (getStatusColor(selectedSubscription) === 'success' ? "#2e7d32" : "#ff9800"),
                        borderWidth: 2,
                        borderStyle: "solid",
                      }}
                      variant="outlined"
                    />,
                  ],
                  ["Subscription Type", selectedSubscription.subscriptionType || "—"],
                  ["Start Date", formatDate(selectedSubscription.startDate)],
                  ["End Date", formatDate(selectedSubscription.endDate)],
                  ["Time Slot", selectedSubscription.timeSlot || "Not specified"],
                ].map(([label, value], i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: "1 1 45%",
                      borderBottom: "1px dashed #eee",
                      pb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: "12px",
                        mb: 0.5,
                        color: "#292929ff",
                      }}
                    >
                      {label} :
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: "12px",
                        color: "#636161",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Weekly Schedule - All days side by side */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "14px",
                    mb: 2,
                    color: "#292929ff",
                  }}
                >
                  Weekly Delivery Schedule :
                </Typography>
                
                <Grid container spacing={1}>
                  {[
                    { day: 'Sunday', short: 'Sun', qty: getDayQuantity('Sunday', selectedSubscription) },
                    { day: 'Monday', short: 'Mon', qty: getDayQuantity('Monday', selectedSubscription) },
                    { day: 'Tuesday', short: 'Tue', qty: getDayQuantity('Tuesday', selectedSubscription) },
                    { day: 'Wednesday', short: 'Wed', qty: getDayQuantity('Wednesday', selectedSubscription) },
                    { day: 'Thursday', short: 'Thu', qty: getDayQuantity('Thursday', selectedSubscription) },
                    { day: 'Friday', short: 'Fri', qty: getDayQuantity('Friday', selectedSubscription) },
                    { day: 'Saturday', short: 'Sat', qty: getDayQuantity('Saturday', selectedSubscription) },
                  ].map(({ day, short, qty }) => (
                    <Grid item xs={4} sm={3} md={12/7} key={day}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: qty > 0 ? '#f8f9fa' : 'transparent',
                          borderColor: qty > 0 ? '#007bff' : '#e0e0e0',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: "11px",
                            color: qty > 0 ? "#007bff" : "#636161",
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          {short}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: "16px",
                            color: qty > 0 ? "#0056b3" : "#636161",
                            lineHeight: 1,
                          }}
                        >
                          {qty || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={capitalizeFirstChar(tabView)} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>
              
              {/* Compact Header Section */}
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} sx={{ px: 2, pt: '10px' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Search subscriptions..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ width: 250 }}
                  />
                </Box>
              </Box>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomDataTable
                  rows={rows}
                  columns={columnsDataTableSubscription}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={handleApplyFilterBtn}
                  toggleFilter={toggleFilter}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={handleInputFocus}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => {}}
                  handleSearch={() => {}}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => {}}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"customSubscriptionNo"}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>
    </>
  );
}

export default Subscription;